<template>
  <div class="ecommerce-status-badge-wrapper" v-if="status">
    <b-badge
      :id="popoverId"
      :class="badgeClass"
      class="ecommerce-status-badge"
      tabindex="0"
    >
      <span class="badge-icon">
        <!-- Verified icon -->
        <svg v-if="status === 'verified'" width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.16667 18.3333L5.5 15.6667L2.5 15L2.83333 11.9167L0.833336 9.58333L2.83333 7.25L2.5 4.16667L5.5 3.5L7.16667 0.833328L10 2.08333L12.8333 0.833328L14.5 3.5L17.5 4.16667L17.1667 7.25L19.1667 9.58333L17.1667 11.9167L17.5 15L14.5 15.6667L12.8333 18.3333L10 17.0833L7.16667 18.3333ZM9.08333 12.5L14.0833 7.5L12.9167 6.33333L9.08333 10.1667L7.08333 8.16667L5.91667 9.33333L9.08333 12.5Z" fill="currentColor"/>
        </svg>
        <!-- Not verified / No account icon -->
        <svg v-else width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.16667 18.3333L5.5 15.6667L2.5 15L2.83333 11.9167L0.833336 9.58333L2.83333 7.25L2.5 4.16667L5.5 3.5L7.16667 0.833328L10 2.08333L12.8333 0.833328L14.5 3.5L17.5 4.16667L17.1667 7.25L19.1667 9.58333L17.1667 11.9167L17.5 15L14.5 15.6667L12.8333 18.3333L10 17.0833L7.16667 18.3333ZM10 13.3333C10.2361 13.3333 10.4375 13.2535 10.6042 13.0938C10.7708 12.934 10.8542 12.7361 10.8542 12.5C10.8542 12.2639 10.7708 12.066 10.6042 11.9063C10.4375 11.7465 10.2361 11.6667 10 11.6667C9.76389 11.6667 9.5625 11.7465 9.39583 11.9063C9.22917 12.066 9.14583 12.2639 9.14583 12.5C9.14583 12.7361 9.22917 12.934 9.39583 13.0938C9.5625 13.2535 9.76389 13.3333 10 13.3333ZM9.16667 10H10.8333V5.83333H9.16667V10Z" fill="currentColor"/>
        </svg>
      </span>
      <span class="badge-text">{{ badgeText }}</span>
    </b-badge>

    <b-popover
      :target="popoverId"
      triggers="hover focus"
      placement="bottom"
      custom-class="ecommerce-status-popover"
    >
      <div class="ecommerce-status-card">
        <div class="card-title">{{ cardTitle }}</div>
        <div class="card-message">{{ cardMessage }}</div>
        <div v-if="status === 'not_verified' && verificationCode" class="verification-code-section">
          <span class="code-label">Verification Code:</span>
          <span class="code-value">{{ verificationCode }}</span>
          <span v-if="copied" class="copied-text">Copied!</span>
          <b-button
            v-else
            variant="link"
            class="copy-btn"
            @click="copyVerificationCode"
            title="Copy code"
          >
            <!-- Copy icon -->
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 22C4.45 22 3.97917 21.8042 3.5875 21.4125C3.19583 21.0208 3 20.55 3 20V6H5V20H16V22H5ZM9 18C8.45 18 7.97917 17.8042 7.5875 17.4125C7.19583 17.0208 7 16.55 7 16V4C7 3.45 7.19583 2.97917 7.5875 2.5875C7.97917 2.19583 8.45 2 9 2H18C18.55 2 19.0208 2.19583 19.4125 2.5875C19.8042 2.97917 20 3.45 20 4V16C20 16.55 19.8042 17.0208 19.4125 17.4125C19.0208 17.8042 18.55 18 18 18H9ZM9 16H18V4H9V16Z" fill="currentColor"/>
            </svg>
          </b-button>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
export default {
  name: 'EcommerceStatusBadge',
  props: {
    ecommerceStatus: {
      type: Object,
      default: null,
    },
    customerId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      copied: false,
    };
  },
  computed: {
    popoverId() {
      return `ecom-status-${this.customerId}`;
    },
    status() {
      return this.ecommerceStatus?.status || null;
    },
    verificationCode() {
      return this.ecommerceStatus?.verification_code || null;
    },
    badgeClass() {
      switch (this.status) {
        case 'verified':
          return 'badge-verified';
        case 'not_verified':
          return 'badge-not-verified';
        case 'no_account':
        default:
          return 'badge-no-account';
      }
    },
    badgeText() {
      switch (this.status) {
        case 'verified':
          return 'Ecom. Verified';
        case 'not_verified':
          return 'Ecom. Not Verified';
        case 'no_account':
        default:
          return 'No Ecom.';
      }
    },
    cardTitle() {
      switch (this.status) {
        case 'verified':
          return 'Ecommerce Account Verified';
        case 'not_verified':
          return 'Ecommerce Account Not Verified';
        case 'no_account':
        default:
          return 'No Ecommerce Account';
      }
    },
    cardMessage() {
      return this.ecommerceStatus?.message || '';
    },
  },
  methods: {
    async copyVerificationCode() {
      if (!this.verificationCode) return;

      try {
        await navigator.clipboard.writeText(this.verificationCode);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/scss/variables.scss';

.ecommerce-status-badge-wrapper {
  display: inline-block;
}

.ecommerce-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 8px 4px 4px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.1;
  cursor: pointer;

  .badge-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;

    i {
      font-size: 16px;
    }
  }

  &.badge-verified {
    background-color: rgba(125, 201, 103, 0.15);
    color: #4CB02F;
  }

  &.badge-not-verified {
    background-color: rgba(255, 157, 0, 0.15);
    color: #FF9D00;
  }

  &.badge-no-account {
    background-color: #F4F4F5;
    color: #7F7F81;
  }
}
</style>

<style lang="scss">
.ecommerce-status-popover {
  .popover-body {
    padding: 0;
  }

  .ecommerce-status-card {
    padding: 14px;
    text-align: center;
    min-width: 235px;

    .card-title {
      font-size: 14px;
      font-weight: 500;
      color: #212721;
      margin-bottom: 8px;
    }

    .card-message {
      font-size: 14px;
      font-weight: 400;
      color: #8F939A;
      line-height: 1.1;
    }

    .verification-code-section {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-top: 8px;
      padding: 8px 12px;
      background-color: #F4F4F5;
      border-radius: 4px;

      .code-label {
        font-size: 14px;
        color: #8F939A;
      }

      .code-value {
        font-size: 14px;
        font-weight: 600;
        color: #212721;
      }

      .copy-btn {
        padding: 0;
        color: #2A2A2A;

        &:hover {
          color: #000;
        }

        i {
          font-size: 20px;
        }
      }

      .copied-text {
        font-size: 12px;
        font-weight: 500;
        color: #4CB02F;
      }
    }
  }
}
</style>
